import { useEffect, useState } from 'react';
import { useServerChanges } from './lib/changes';
import { currentQuarter } from './lib/dates';
import { api, type Me, type PhysicianSummary, type Site, type Tenant } from './lib/api';
import { configure, isConfigured, signOut, signedInEmail } from './lib/auth';
import { match, navigate, usePath } from './lib/router';
import { MyColleagues } from './pages/MyColleagues';
import { MyShifts } from './pages/MyShifts';
import { WhoAreYou } from './pages/WhoAreYou';
import { AdminGrid } from './pages/AdminGrid';
import { SiteWeek } from './pages/SiteWeek';
import { WeekBuilder } from './pages/WeekBuilder';
import { QuarterDraft } from './pages/QuarterDraft';
import { AdminRequests } from './pages/AdminRequests';
import { Requests } from './pages/Requests';
import { Login } from './pages/Login';
import { AllHospitals } from './pages/AllHospitals';
import { Dashboard } from './pages/Dashboard';
import { Hospital } from './pages/Hospital';
import { AdminTimesheets } from './pages/AdminTimesheets';
import { Timesheets } from './pages/Timesheets';
import { Logistics } from './pages/Logistics';
import { Residences } from './pages/Residences';
import { HousingCapacity } from './pages/HousingCapacity';
import { Organization } from './pages/Organization';
import { CallieCosts } from './pages/CallieCosts';
import { Evaluate } from './pages/Evaluate';
import { ResidencyBlocks, ResidencyRoles, ResidencyRoster } from './pages/Residency';
import { ResidencyImport } from './pages/ResidencyImport';
import { ResidencyHome } from './pages/ResidencyHome';
import { Roster } from './pages/Roster';
import { Tour } from './pages/Tour';
import { TransferCenter, TransferCenterBoard } from './pages/TransferCenter';
import { Callie } from './components/Callie';

const ME_KEY = 'openmd.me';
const ADMIN_KEY = 'openmd.admin';
/** Which customer an owner is working in (D49). Remembered per device, like the two above. */
const CLIENT_KEY = 'openmd.client';

/**
 * CallCovered: two faces on one address — the physician's pages at "/", "/tour", "/requests" and "/timesheets",
 * the scheduler's under "/admin". Who is looking comes from the login
 * (D30): the API's /me says whether the caller is a scheduler and which
 * physician their login is tied to. When login is not configured (local
 * development), the old stand-ins remain: a physician picker and a typed
 * name for the audit log, both remembered on the device.
 */
export function App() {
  const path = usePath();
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [sites, setSites] = useState<Site[] | null>(null);
  const [physicians, setPhysicians] = useState<PhysicianSummary[] | null>(null);
  const [suggestedQuarter, setSuggestedQuarter] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState<string | null>(() => read(ME_KEY));
  const [clientPick, setClientPick] = useState<string | null>(() => read(CLIENT_KEY));
  const [typedName, setTypedName] = useState<string>(() => read(ADMIN_KEY) ?? '');

  // 1. Is login switched on, and do we hold a session?
  useEffect(() => {
    api
      .authConfig()
      .then((c) => {
        configure(c);
        setSignedIn(c.mode !== 'cognito' || !!signedInEmail());
        setReady(true);
      })
      .catch((e: Error) => setError(e.message));
    const onSignedOut = () => setSignedIn(false);
    window.addEventListener('openmd:signed-out', onSignedOut);
    return () => window.removeEventListener('openmd:signed-out', onSignedOut);
  }, []);

  // 2. Once signed in (or open), who am I and what is there to show.
  useEffect(() => {
    if (!ready || !signedIn) return;
    setMe(null);
    Promise.all([api.me(), api.sites()])
      .then(async ([m, s]) => {
        setMe(m);
        setSites(s);
        // The directory is only needed by the local-mode picker and by schedulers' and coordinators' dialogs;
        // the quarter list tells the grid where the data is (today's quarter is usually empty).
        if (m.admin) {
          const [list, q] = await Promise.all([api.physicians(), api.gridQuarters()]);
          setPhysicians(list);
          setSuggestedQuarter(q.suggested);
        } else if (m.logistics) {
          setPhysicians(await api.physicians());
        } else {
          setPhysicians([]);
        }
      })
      .catch((e: Error) => setError(e.message));
  }, [ready, signedIn]);

  const choose = (abbreviation: string | null) => {
    write(ME_KEY, abbreviation);
    setPick(abbreviation);
  };
  const setAdminName = (name: string) => {
    write(ADMIN_KEY, name || null);
    setTypedName(name);
  };
  const leave = () => {
    signOut();
    setSignedIn(false);
    setMe(null);
    navigate('/');
  };

  // Whether this login may sign up for Transfer Center shifts (D108). Asked once, because the
  // tab must not appear for the great majority of physicians who are not credentialed there —
  // a board of shifts they cannot work is noise, and an invitation refused at the last step.
  const [transferSlots, setTransferSlots] = useState<string[] | null>(null);
  // Who this is has TWO sources: the login's own abbreviation when Cognito is on, and the
  // local picker when it is not. Asking only the first leaves the tab permanently missing in
  // development, which is how D101's unreachable screen happened.
  const whoAmI = me?.physician?.abbreviation ?? pick;
  useEffect(() => {
    if (!signedIn || !whoAmI) {
      setTransferSlots(null);
      return;
    }
    let live = true;
    api
      .transferCenterEligibility(whoAmI)
      .then((r) => live && setTransferSlots(r.slots))
      .catch(() => live && setTransferSlots([]));
    return () => {
      live = false;
    };
  }, [signedIn, whoAmI]);

  const admin = path === '/admin' || path.startsWith('/admin/');
  const logisticsPage = path.startsWith('/admin/logistics');
  // A logistics coordinator's home is the logistics page, not a physician's shift list.
  useEffect(() => {
    if (me && !me.admin && me.logistics && !me.physician && !admin) navigate('/admin/logistics');
    const evaluator = me && !me.admin && !me.physician && (me.programRoles ?? []).some((p) => p.capabilities.includes('performance.record'));
    if (evaluator && !admin) navigate('/admin/evaluate');
  }, [me, admin]);
  // Somebody else's change, on somebody else's machine (D94). Asked for every twenty seconds
  // while this tab is being looked at, and raised as the same announcement every screen already
  // listens to. Above the early returns below, because a hook cannot be called conditionally.
  useServerChanges(signedIn && !!me);
  if (error) return <Shell admin={admin}><p className="error">The schedule service isn't answering right now ({error}). Try again in a moment.</p></Shell>;
  if (!ready) return <Shell admin={admin}><p className="muted">Loading…</p></Shell>;
  if (!signedIn) return <Shell><Login onSignedIn={() => setSignedIn(true)} /></Shell>;
  if (!me || !sites || !physicians) return <Shell admin={admin}><p className="muted">Loading…</p></Shell>;

  const loginOn = isConfigured();
  const whoLabel = loginOn ? `${me.name ?? me.email ?? ''}${me.owner ? ' · CallCovered' : ''}` : '';
  const editedBy = loginOn ? (me.name ?? me.email ?? 'unknown') : typedName;
  // The tabs follow the KIND of work in the caller's reach, not the client
  // (D38). One client can hold both a residency and an attending rota — NYU
  // does on day one — so a per-client list would be wrong the moment there is
  // a second customer. `me.engines` names the engines of the schedules this
  // login can see; each engine brings its own screens.
  //
  // Which schedules those are is now narrowed by the customer being worked
  // in (D49). For a customer's own people that changes nothing: they are in
  // exactly one, and the picker below does not appear. For an owner it is
  // the difference between a usable navigation and the union of every
  // product CallCovered sells.
  const tenants = me.tenants ?? [];
  const tenant = tenants.find((t) => t.id === clientPick) ?? (tenants.length === 1 ? tenants[0] : null);
  const engines = tenant ? tenant.engines : me.engines;
  // The residency screens take the list to look in. Naming the chosen
  // customer stops them searching for the first one that happens to run a
  // residency, which is how they found NYU before anyone had said so.
  const scopedClients = tenant ? [tenant.id] : me.clients;
  // Which home page /admin lands on. A customer running only a residency
  // gets the program's; one running an attending rota, or both, keeps the
  // week it already had.
  const residencyHome = engines.includes('im-residency') && !engines.includes('hospitalist-shift');
  const chooseClient = (id: string | null) => {
    write(CLIENT_KEY, id);
    setClientPick(id);
    // Back to the home page: the screen being looked at belongs to the
    // customer being left, and its ids mean nothing in the new one.
    navigate('/admin');
  };
  // A login that holds a program role but is not a scheduler — a faculty
  // evaluator — has exactly one screen, and reaching it must not require
  // being a scheduler. Its own page decides what that role may do; this only
  // decides whether the screen exists for them.
  const mayEvaluate = (me.programRoles ?? []).some((p) => p.capabilities.includes('performance.record'));
  const evaluateOnly = mayEvaluate && !me.admin;
  const adminTabs: [string, string, boolean][] = evaluateOnly
    ? [['/admin/evaluate', 'Performance assessment', path === '/admin/evaluate']]
    : [
        ['/admin', 'Home', path === '/admin'],
        ...tabsForEngines(engines, path, logisticsPage),
        ...(mayEvaluate ? ([['/admin/evaluate', 'Performance assessment', path === '/admin/evaluate']] as [string, string, boolean][]) : []),
        // Every client has an organization to maintain, whatever it schedules.
        ['/admin/org', 'Organization', path === '/admin/org'],
        // What Callie costs us to run (D91). CallCovered's own figure, so only an owner is
        // offered it — the route behind it refuses anybody else regardless.
        ...(me.owner ? ([['/admin/callie-costs', 'Callie costs', path === '/admin/callie-costs']] as [string, string, boolean][]) : []),
      ];
  const logisticsTabs: [string, string, boolean][] = [
    ['/admin/logistics', 'Tour logistics', path === '/admin/logistics'],
    ['/admin/logistics/residences', 'Residences & guide', path === '/admin/logistics/residences'],
    ['/admin/logistics/housing', 'Housing capacity', path === '/admin/logistics/housing'],
    ['/admin/logistics/transfer-center', 'Transfer Center', path === '/admin/logistics/transfer-center'],
  ];

  if (admin) {
    if (!me.admin && !(me.logistics && logisticsPage) && !(mayEvaluate && path === '/admin/evaluate')) {
      return (
        <Shell who={whoLabel} onLeave={leave}>
          <p className="error">The scheduling pages are for schedulers. <button className="linklike" type="button" onClick={() => navigate('/')}>Back to my shifts</button></p>
        </Shell>
      );
    }
    // Hiding a tab is not access control: a screen that belongs to a kind of
    // work this login does not do is refused outright, so typing the address
    // does not land on an empty page that looks broken (D38). The API scopes
    // the data regardless; this is so the answer reads as a decision.
    // A hospital's week page lives under /admin/sites/… and belongs to the Grid tab (it is reached from the grid and the dashboard).
    const owned = adminTabs.flatMap(([to]) => (to === '/admin/grid' ? ['/admin/grid', '/admin/sites'] : [to]));
    if (!owned.some((to) => to === path || (to !== '/admin' && path.startsWith(`${to}/`))) && path !== '/admin') {
      return (
        <Shell admin who={whoLabel} onLeave={leave} client={<ClientPicker tenants={tenants} chosen={tenant?.id ?? null} onChoose={chooseClient} />} nav={<Nav items={adminTabs} />}>
          <p className="error">
            That screen belongs to a kind of scheduling this organization doesn't do.{' '}
            <button className="linklike" type="button" onClick={() => navigate('/admin')}>Back to the home page</button>
          </p>
        </Shell>
      );
    }
    const week = match('/admin/sites/:siteId/periods/:periodStart', path);
    const build = match('/admin/sites/:siteId/periods/:periodStart/build', path);
    const hospital = match('/admin/hospitals/:siteId', path);
    const params = new URLSearchParams(window.location.search);
    const quarter = params.get('quarter') ?? suggestedQuarter ?? defaultQuarter();
    const firstSite = sites[0]?.id ?? 'randolph';
    const page = build ? (
      <WeekBuilder siteId={build.siteId} periodStart={build.periodStart} sites={sites} editedBy={editedBy} />
    ) : week ? (
      <SiteWeek siteId={week.siteId} periodStart={week.periodStart} sites={sites} editedBy={editedBy} />
    ) : path === '/admin/logistics' ? (
      <Logistics siteId={params.get('siteId') ?? firstSite} week={params.get('week') ?? nextTuesday()} sites={sites} physicians={physicians} />
    ) : path === '/admin/logistics/residences' ? (
      <Residences siteId={params.get('siteId') ?? firstSite} sites={sites} />
    ) : path === '/admin/logistics/transfer-center' ? (
      <TransferCenterBoard />
    ) : path === '/admin/logistics/housing' ? (
      <HousingCapacity week={params.get('week') ?? nextTuesday()} />
    ) : path.startsWith('/admin/roster') ? (
      <Roster abbreviation={match('/admin/roster/:abbreviation', path)?.abbreviation ? decodeURIComponent(match('/admin/roster/:abbreviation', path)!.abbreviation) : null} editedBy={editedBy} />
    ) : path === '/admin/residency/import' ? (
      <ResidencyImport clients={scopedClients} scheduleId={params.get('scheduleId')} year={params.get('year')} />
    ) : path === '/admin/residency/roles' ? (
      <ResidencyRoles clients={scopedClients} scheduleId={params.get('scheduleId')} year={params.get('year')} editedBy={editedBy} myRoles={me.programRoles ?? []} owner={me.owner} />
    ) : path === '/admin/residency/blocks' ? (
      <ResidencyBlocks clients={scopedClients} scheduleId={params.get('scheduleId')} year={params.get('year')} block={params.get('block')} />
    ) : path === '/admin/residency' ? (
      <ResidencyRoster clients={scopedClients} scheduleId={params.get('scheduleId')} year={params.get('year')} physicianId={params.get('physicianId')} />
    ) : path === '/admin/evaluate' ? (
      <Evaluate scheduleId={params.get('scheduleId')} />
    ) : path === '/admin/callie-costs' ? (
      <CallieCosts />
    ) : path === '/admin/org' ? (
      <Organization clientId={params.get('clientId') ?? tenant?.id ?? null} unitId={params.get('unitId')} owner={me.owner} />
    ) : path === '/admin/requests' ? (
      <AdminRequests quarter={quarter} />
    ) : path === '/admin/draft' ? (
      <QuarterDraft quarter={quarter} sites={sites} editedBy={editedBy} />
    ) : path === '/admin/grid' ? (
      <AdminGrid quarter={quarter} />
    ) : hospital ? (
      <Hospital siteId={hospital.siteId} quarter={params.get('quarter') ?? currentQuarter()} sites={sites} editedBy={editedBy} />
    ) : path === '/admin/hospitals' ? (
      <Hospital siteId={params.get('siteId') ?? firstSite} quarter={params.get('quarter') ?? currentQuarter()} sites={sites} editedBy={editedBy} />
    ) : path === '/admin/timesheets' ? (
      <AdminTimesheets siteId={params.get('siteId') ?? firstSite} periodStart={params.get('periodStart') ?? lastTuesday()} sites={sites} editedBy={editedBy} />
    ) : path === '/admin/dashboard' ? (
      // What used to be the home page. It answers "what needs me today"; the home page now
      // answers "what does the month look like everywhere" (D65).
      <Dashboard week={params.get('week') ?? undefined} sites={sites} editedBy={editedBy} />
    ) : residencyHome ? (
      // A residency has no week — it has a block, and the hospitalist
      // dashboard renders as an empty one with the wrong words in it (D50).
      // Only when the customer in hand runs a residency and nothing else:
      // somebody who does both keeps the week, because they have one.
      <ResidencyHome clients={scopedClients} scheduleId={params.get('scheduleId')} year={params.get('year')} />
    ) : (
      // Home: every hospital's month at once, one grid each (D65).
      <AllHospitals quarter={params.get('quarter') ?? currentQuarter()} sites={sites} editedBy={editedBy} />
    );
    return (
      <Shell
        admin
        section={me.admin ? 'Scheduling' : 'Logistics'}
        who={whoLabel}
        onLeave={loginOn ? leave : undefined}
        editedBy={loginOn ? undefined : typedName}
        onEditedBy={loginOn ? undefined : setAdminName}
        client={<ClientPicker tenants={tenants} chosen={tenant?.id ?? null} onChoose={chooseClient} />}
        nav={<Nav items={[...(me.admin ? adminTabs : logisticsTabs), ...(me.physician ? [['/', 'My shifts', false] as [string, string, boolean]] : [])]} query={{ '/admin/grid': `?quarter=${quarter}`, '/admin/requests': `?quarter=${quarter}` }} />}
      >
        {page}
        <Callie abbreviation={null} editedBy={loginOn ? null : typedName} hint={me.admin ? 'Ask about a week, a hospital, or who could fill a position — or ask for a draft of the logistics note. Try: "Who could fill Rounder 4 at Randolph on Thursday?"' : 'Ask about this week\'s logistics — rotation, rooms, rides. Try: "Who still needs a room at Randolph the week of Feb 2?"'} />
      </Shell>
    );
  }

  // Physician pages: the login says who; locally, the picker does.
  let person: PhysicianSummary | null = me.physician;
  if (!loginOn) {
    person = pick ? physicians.find((p) => p.abbreviation === pick) ?? null : null;
    if (!person) return <Shell><WhoAreYou physicians={physicians} onChoose={choose} /></Shell>;
  }
  /**
   * A physician the roster has marked Inactive (D95). The API refuses them everything but this
   * answer, so the explanation has to live here — a sentence and who to ring, rather than the
   * error page they would otherwise meet on a login that worked yesterday. Somebody who also
   * schedules keeps their office pages; only a physician-only login lands here.
   */
  if (me.physician?.status === 'Inactive' && !me.admin && !me.logistics) {
    return (
      <Shell who={whoLabel} onLeave={leave}>
        <section className="card">
          <h1>Your access has ended</h1>
          <p>
            The roster has {me.physician.firstName} {me.physician.lastName} as no longer working, so the schedule, trips and
            timesheets are closed to this login.
          </p>
          <p className="muted">
            If that is wrong, ask the scheduling office to make you active again{me.officeEmail ? <> — <a href={`mailto:${me.officeEmail}`}>{me.officeEmail}</a></> : null}. Nothing has been deleted.
          </p>
        </section>
      </Shell>
    );
  }

  if (!person) {
    return (
      <Shell who={whoLabel} onLeave={leave} nav={me.admin ? <Nav items={adminTabs.map(([to, label]) => [to, label, false])} /> : me.logistics ? <Nav items={logisticsTabs.map(([to, label]) => [to, label, false])} /> : undefined}>
        <section className="card">
          <h1>{me.admin ? 'Scheduler login' : me.logistics ? 'Logistics login' : 'No physician page for this login'}</h1>
          <p className="muted">
            {me.unknownAbbreviation
              ? `This login is tied to "${me.unknownAbbreviation}", which is not on the roster. Ask your scheduler to check the abbreviation.`
              : me.admin
                ? 'Use the scheduling pages above.'
                : me.logistics
                  ? 'Use the logistics pages above.'
                  : 'This login is not tied to a physician on the roster yet. Ask your scheduler.'}
          </p>
        </section>
      </Shell>
    );
  }
  const requests = path === '/requests';
  const sheets = path === '/timesheets';
  const tour = path === '/tour';
  const colleagues = path === '/colleagues';
  // Only for the few physicians credentialed at the Transfer Center (D108). `transferSlots` is
  // null until the answer is back, so the tab appears once rather than flickering in.
  const transfer = path === '/transfer-center';
  const mayTransfer = (transferSlots ?? []).length > 0;
  return (
    <Shell
      who={`${person.firstName} ${person.lastName}`}
      onLeave={loginOn ? leave : undefined}
      onChange={loginOn ? undefined : () => choose(null)}
      nav={<Nav items={[['/', 'My shifts', !requests && !sheets && !tour && !colleagues && !transfer], ['/colleagues', 'My colleagues', colleagues], ['/tour', 'My trip', tour], ['/requests', 'Requests', requests], ...(mayTransfer ? [['/transfer-center', 'Transfer Center', transfer] as [string, string, boolean]] : []), ['/timesheets', 'Timesheets', sheets], ...(me.admin ? [['/admin', 'Scheduling', false] as [string, string, boolean]] : [])]} />}
    >
      {requests ? <Requests physician={person} /> : sheets ? <Timesheets physician={person} officeEmail={me.officeEmail ?? null} /> : tour ? <Tour physician={person} /> : colleagues ? <MyColleagues abbreviation={person.abbreviation} /> : transfer ? <TransferCenter abbreviation={person.abbreviation} /> : <MyShifts physician={person} sites={sites} />}
      <Callie abbreviation={loginOn ? null : person.abbreviation} editedBy={null} hint={'Ask in plain language. Try: "I can\'t work the week of March 3", "Where am I staying next week?", or "When is my next shift?"'} />
    </Shell>
  );
}

/** The pages each kind of user moves between. */
function Nav({ items, query = {} }: { items: [string, string, boolean][]; query?: Record<string, string> }) {
  return (
    <nav className="tabs" aria-label="Pages">
      {items.map(([to, label, active]) => (
        <button key={to} type="button" className={active ? 'tab active' : 'tab'} aria-current={active ? 'page' : undefined} onClick={() => navigate(`${to}${query[to] ?? ''}`)}>{label}</button>
      ))}
    </nav>
  );
}

/**
 * Which customer an owner is working in (D49).
 *
 * Only shown to somebody who is in more than one. A customer's own
 * schedulers are in exactly one and should never see a control implying
 * otherwise — it would be the only place in the app hinting that other
 * customers exist.
 *
 * "All customers" stays on the list because an owner genuinely does need it:
 * it is how the organization screen shows the whole estate, and how somebody
 * setting up a new customer reaches it before it has any schedules.
 */
function ClientPicker({ tenants, chosen, onChoose }: { tenants: Tenant[]; chosen: string | null; onChoose: (id: string | null) => void }) {
  if (tenants.length < 2) return null;
  return (
    <label className="clientpick">
      <span className="sr-only">Customer</span>
      <select value={chosen ?? ''} onChange={(e) => onChoose(e.target.value || null)} aria-label="Customer">
        <option value="">All customers</option>
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
    </label>
  );
}

/** The coming Tuesday (today if it is one): the handoff the logistics page is about. */
function nextTuesday(): string {
  const d = new Date();
  const ahead = (2 - d.getUTCDay() + 7) % 7;
  d.setUTCDate(d.getUTCDate() + ahead);
  return d.toISOString().slice(0, 10);
}

/** The most recent Tuesday (today if it is one): the tour most likely to need timesheets. */
function lastTuesday(): string {
  const d = new Date();
  const back = (d.getUTCDay() - 2 + 7) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

function Shell({ children, who, onChange, onLeave, admin, section = 'Scheduling', editedBy, onEditedBy, nav, client }: { children: React.ReactNode; who?: string; onChange?: () => void; onLeave?: () => void; admin?: boolean; section?: string; editedBy?: string; onEditedBy?: (name: string) => void; nav?: React.ReactNode; client?: React.ReactNode }) {
  return (
    <div className={`shell${admin ? ' admin' : ''}`}>
      <header className="topbar">
        <div className="brand">
          <button className="brandbtn" type="button" onClick={() => navigate(admin ? '/admin' : '/')} aria-label="CallCovered — home">
            <img className="cc-mark" src="/brand/cc-mark.svg" alt="" />
            <span className="wordmark">CallCovered</span>
            {admin && <span className="product where">{section}</span>}
          </button>
          {client}
          {nav}
        </div>
        {who && (
          <div className="who">
            <span>{who}</span>
            {onChange && <button className="linklike" onClick={onChange} type="button">not you?</button>}
            {onLeave && <button className="linklike" onClick={onLeave} type="button">sign out</button>}
          </div>
        )}
        {admin && onEditedBy && (
          <label className="who">
            <span className="muted">Edits recorded as</span>
            <input className="adminname" value={editedBy ?? ''} onChange={(e) => onEditedBy(e.target.value)} placeholder="your name" />
          </label>
        )}
      </header>
      <main className={`content${admin ? ' wide' : ''}`}>{children}</main>
      <footer className="foot">
        <span>{admin ? 'Every change is checked against the rules and recorded with who made it and why.' : 'Published schedules only. Questions about a shift go to your scheduler.'}</span>
        <span className="tagline">CallCovered · Always covered. Always reliable.</span>
      </footer>
    </div>
  );
}

function defaultQuarter(): string {
  // Current quarter by the calendar; the grid shows whatever exists for it.
  const d = new Date();
  return `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable: the value just lives in memory */
  }
}

/**
 * The screens each kind of work brings (D38).
 *
 * `hospitalist-shift` is what Open Source MD runs: a quarter grid across
 * hospitals, per-hospital months, recorded hours, quarterly availability, and
 * the travel and lodging that go with physicians who move between sites.
 *
 * `im-residency` is the NYULISOM engine — two-week blocks, twenty-five to a
 * year. Its reading screens are read-only (D43): a program's block schedule is
 * the program's own document, uploaded whole and corrected by uploading a
 * corrected file, so there is nothing here to edit and nowhere for a second
 * version of the truth to live. "Upload a year" (D48) is that one door in,
 * and it writes nothing without showing what it would do first.
 */
function tabsForEngines(engines: string[], path: string, logisticsPage: boolean): [string, string, boolean][] {
  const byEngine: Record<string, [string, string, boolean][]> = {
    'hospitalist-shift': [
      // What used to be the home page, now under its own tab: it answers "what needs me
      // today", while Home answers "what does the month look like everywhere" (D65).
      ['/admin/dashboard', 'Dashboard', path === '/admin/dashboard'],
      ['/admin/grid', 'Grid', path === '/admin/grid' || path.startsWith('/admin/sites/')],
      // Its own tab, not a link buried on the grid: drafting a quarter is one of the
      // handful of things the office comes here to DO, and a screen nobody can find is
      // not a screen (D42 step 4).
      ['/admin/draft', 'Draft', path === '/admin/draft'],
      ['/admin/hospitals', 'Hospitals', path.startsWith('/admin/hospitals')],
      ['/admin/timesheets', 'Timesheets', path === '/admin/timesheets'],
      ['/admin/requests', 'Requests', path === '/admin/requests'],
      ['/admin/logistics', 'Logistics', logisticsPage],
      // The roster screen (D39) is shaped around the roster sheet Open Source
      // MD keeps — credentialing by site, state licences, positions. Whether a
      // residency wants this screen or one of its own is the roster's author's
      // call, so it sits here rather than being assumed universal.
      ['/admin/roster', 'Roster', path.startsWith('/admin/roster')],
    ],
    'im-residency': [
      ['/admin/residency', 'Residents', path === '/admin/residency'],
      ['/admin/residency/blocks', 'Block board', path.startsWith('/admin/residency/blocks')],
      ['/admin/residency/import', 'Upload a year', path.startsWith('/admin/residency/import')],
      ['/admin/residency/roles', 'Who runs it', path.startsWith('/admin/residency/roles')],
    ],
  };
  const seen = new Set<string>();
  const out: [string, string, boolean][] = [];
  for (const engine of engines) {
    for (const tab of byEngine[engine] ?? []) {
      if (seen.has(tab[0])) continue; // two engines asking for the same screen list it once
      seen.add(tab[0]);
      out.push(tab);
    }
  }
  return out;
}
